import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

const GOLD = '#d4af37';

interface DonatePageContent {
  title: string;
  subtitle: string;
  mainDescription: string;
  suggestedLevelsTitle: string;
  level1: { title: string; amount: string; description: string };
  level2: { title: string; amount: string; description: string };
  level3: { title: string; amount: string; description: string };
  usageTitle: string;
  usageDescription: string;
  onlineDonationTitle: string;
  onlineDonationDescription: string;
  bankTransferTitle: string;
  bankTransferDescription: string;
  impactTitle: string;
  impactDescription: string;
}

export default function DonatePageManager() {
  const [content, setContent] = useState<DonatePageContent>({
    title: 'Support the Temple — Overview',
    subtitle: 'Your contributions sustain our sacred traditions and community service',
    mainDescription: 'Your donations sustain daily rituals, annadanam (community meals), temple maintenance, and festival arrangements. We offer multiple ways to contribute — one-time gifts, recurring donations, project-specific funding, and in-kind support.',
    suggestedLevelsTitle: 'Suggested Giving Levels',
    level1: { title: 'Annadanam Pack', amount: 'Rs 500', description: 'Provides meals for a family of four.' },
    level2: { title: 'Renovation Contribution', amount: 'Rs 5,000', description: 'Supports minor repairs and materials.' },
    level3: { title: 'Festival Sponsor', amount: 'Rs 20,000', description: 'Helps underwrite festival costs and arrangements.' },
    usageTitle: 'How Donations Are Used',
    usageDescription: 'We maintain transparent accounting and allocate funds to ceremonies, community services, structural upkeep, priest support, and festival activities. Donors receive acknowledgements and project reports on request.',
    onlineDonationTitle: 'One-time Online Donation',
    onlineDonationDescription: 'Use our secure online gateway to make a single donation. Please include your name and purpose in the form.',
    bankTransferTitle: 'Bank Transfer & Collections',
    bankTransferDescription: 'For larger gifts or recurring support, contact the temple office for bank details and receipts.',
    impactTitle: 'Impact Stories',
    impactDescription: 'Brief examples of how donor support has enabled free meals, repaired temple structures, and funded festival lighting and cultural programs. Donor-named plaques are placed for major contributions.'
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    const unsubscribe = loadContent();
    return () => unsubscribe?.();
  }, []);

  const loadContent = () => {
    try {
      console.log('🔄 DonatePageManager: Setting up real-time listener...');
      const docRef = doc(db, 'donate_page_content', 'main');
      setLoading(true);
      
      const unsubscribe = onSnapshot(docRef, (docSnap) => {
        try {
          console.group('📊 DonatePageManager Listener Event');
          console.log('Document exists:', docSnap.exists());
          console.log('Document ID:', docSnap.id);
          console.log('Raw data:', docSnap.data());
          console.groupEnd();
          
          if (docSnap.exists()) {
            const data = docSnap.data() as DonatePageContent;
            setContent(data);
            console.log('✅ Admin: Real-time content loaded from Firebase');
          } else {
            console.warn('⚠️ Admin: Document does not exist, using defaults');
          }
        } catch (error) {
          console.error('Error processing content:', error);
        } finally {
          setLoading(false);
        }
      }, (error) => {
        console.error('❌ Admin Error in onSnapshot:', error);
        console.error('Error code:', (error as any).code);
        setSnackbar({ open: true, message: 'Error loading content', severity: 'error' });
        setLoading(false);
      });
      
      return unsubscribe;
    } catch (error) {
      console.error('Error setting up listener:', error);
      setSnackbar({ open: true, message: 'Error loading content', severity: 'error' });
      setLoading(false);
      return () => {};
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      console.log('💾 Saving donate page content to Firebase...');
      console.log('Full content object:', JSON.stringify(content, null, 2));
      console.log('Subtitle being saved:', content.subtitle);
      
      const docRef = doc(db, 'donate_page_content', 'main');
      await setDoc(docRef, content);
      
      console.log('✅ Content saved successfully to donate_page_content/main');
      console.log('Document reference:', docRef.path);
      setSnackbar({ open: true, message: 'Content saved successfully!', severity: 'success' });
    } catch (error) {
      console.error('❌ Error saving content:', error);
      setSnackbar({ open: true, message: 'Error saving content', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof DonatePageContent, value: string | any) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  const handleLevelChange = (levelKey: 'level1' | 'level2' | 'level3', field: 'title' | 'amount' | 'description', value: string) => {
    setContent(prev => ({
      ...prev,
      [levelKey]: { ...prev[levelKey], [field]: value }
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
            Donate Page Manager
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Manage the main donation page content
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadContent}
            disabled={loading}
            variant="outlined"
            sx={{ 
              borderColor: GOLD, 
              color: GOLD,
              '&:hover': { bgcolor: `${GOLD}15` }
            }}
          >
            Refresh
          </Button>
          <Button
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      {/* Main Page Content */}
      <Paper elevation={0} sx={{ 
        p: 3, 
        mb: 3,
        border: `2px solid ${GOLD}`,
        borderRadius: 2,
        bgcolor: '#fafaf8'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          📄 Main Page Content
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Page Title"
              value={content.title}
              onChange={(e) => handleChange('title', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: GOLD },
                  '&.Mui-focused fieldset': { borderColor: GOLD }
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Page Subtitle"
              value={content.subtitle}
              onChange={(e) => handleChange('subtitle', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: GOLD },
                  '&.Mui-focused fieldset': { borderColor: GOLD }
                }
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Giving Levels */}
      <Paper elevation={0} sx={{ 
        p: 3, 
        mb: 3,
        border: `2px solid ${GOLD}`,
        borderRadius: 2,
        bgcolor: '#fafaf8'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          💰 Suggested Giving Levels
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Levels Section Title"
              value={content.suggestedLevelsTitle}
              onChange={(e) => handleChange('suggestedLevelsTitle', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: GOLD },
                  '&.Mui-focused fieldset': { borderColor: GOLD }
                }
              }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          {['level1', 'level2', 'level3'].map((levelKey: any) => (
            <Grid item xs={12} sm={6} md={4} key={levelKey}>
              <Card elevation={0} sx={{ border: `1px solid ${GOLD}40` }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: GOLD, mb: 1 }}>
                    Level {levelKey === 'level1' ? '1' : levelKey === 'level2' ? '2' : '3'}
                  </Typography>
                  <TextField
                    fullWidth
                    label="Title"
                    value={content[levelKey].title}
                    onChange={(e) => handleLevelChange(levelKey, 'title', e.target.value)}
                    size="small"
                    sx={{ mb: 1, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                  />
                  <TextField
                    fullWidth
                    label="Amount"
                    value={content[levelKey].amount}
                    onChange={(e) => handleLevelChange(levelKey, 'amount', e.target.value)}
                    size="small"
                    sx={{ mb: 1, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                  />
                  <TextField
                    fullWidth
                    label="Description"
                    value={content[levelKey].description}
                    onChange={(e) => handleLevelChange(levelKey, 'description', e.target.value)}
                    multiline
                    rows={2}
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Usage Section */}
      <Paper elevation={0} sx={{ 
        p: 3, 
        mb: 3,
        border: `2px solid ${GOLD}`,
        borderRadius: 2,
        bgcolor: '#fafaf8'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          📊 How Donations Are Used
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Section Title"
              value={content.usageTitle}
              onChange={(e) => handleChange('usageTitle', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: GOLD },
                  '&.Mui-focused fieldset': { borderColor: GOLD }
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={content.usageDescription}
              onChange={(e) => handleChange('usageDescription', e.target.value)}
              multiline
              rows={3}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: GOLD },
                  '&.Mui-focused fieldset': { borderColor: GOLD }
                }
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Donation Methods */}
      <Paper elevation={0} sx={{ 
        p: 3, 
        mb: 3,
        border: `2px solid ${GOLD}`,
        borderRadius: 2,
        bgcolor: '#fafaf8'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          💳 Donation Methods
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Card elevation={0} sx={{ border: `1px solid ${GOLD}40` }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: GOLD, mb: 1 }}>
                  Online Donation
                </Typography>
                <TextField
                  fullWidth
                  label="Title"
                  value={content.onlineDonationTitle}
                  onChange={(e) => handleChange('onlineDonationTitle', e.target.value)}
                  size="small"
                  sx={{ mb: 1, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                />
                <TextField
                  fullWidth
                  label="Description"
                  value={content.onlineDonationDescription}
                  onChange={(e) => handleChange('onlineDonationDescription', e.target.value)}
                  multiline
                  rows={2}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Card elevation={0} sx={{ border: `1px solid ${GOLD}40` }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: GOLD, mb: 1 }}>
                  Bank Transfer
                </Typography>
                <TextField
                  fullWidth
                  label="Title"
                  value={content.bankTransferTitle}
                  onChange={(e) => handleChange('bankTransferTitle', e.target.value)}
                  size="small"
                  sx={{ mb: 1, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                />
                <TextField
                  fullWidth
                  label="Description"
                  value={content.bankTransferDescription}
                  onChange={(e) => handleChange('bankTransferDescription', e.target.value)}
                  multiline
                  rows={2}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Impact Section */}
      <Paper elevation={0} sx={{ 
        p: 3,
        border: `2px solid ${GOLD}`,
        borderRadius: 2,
        bgcolor: '#fafaf8'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          ⭐ Impact Stories
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Section Title"
              value={content.impactTitle}
              onChange={(e) => handleChange('impactTitle', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: GOLD },
                  '&.Mui-focused fieldset': { borderColor: GOLD }
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={content.impactDescription}
              onChange={(e) => handleChange('impactDescription', e.target.value)}
              multiline
              rows={3}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: GOLD },
                  '&.Mui-focused fieldset': { borderColor: GOLD }
                }
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity as 'success' | 'error'}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
